import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase/config';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import PostCard from '../components/PostCard';
import Comment from '../components/Comment';
import CommentForm from '../components/CommentForm';
import { ArrowLeft, MessageSquare } from 'lucide-react';

const PostPage = ({ navigate, postId }) => {
    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!postId) return;
        const postRef = doc(db, "posts", postId);
        const unsubPost = onSnapshot(postRef, (docSnap) => {
            if (docSnap.exists()) {
                const postData = { id: docSnap.id, ...docSnap.data() };
                const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
                if (postData.createdAt?.toDate && postData.createdAt.toDate() < twentyFourHoursAgo) {
                    setPost(null);
                } else {
                    setPost(postData);
                }
            } else {
                setPost(null);
            }
            setLoading(false);
        });

        const commentsQuery = query(collection(db, "comments"), where("postId", "==", postId));
        const unsubComments = onSnapshot(commentsQuery, (snapshot) => {
            const commentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            commentsData.sort((a, b) => (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0));
            setComments(commentsData);
        });

        return () => { unsubPost(); unsubComments(); };
    }, [postId]);

    // Build comment trees for nested replies
    const commentThreads = useMemo(() => {
        const commentMap = {};
        const topLevel = [];

        comments.forEach(c => {
            commentMap[c.id] = { ...c, replies: [] };
        });

        comments.forEach(c => {
            if (c.parentCommentId && commentMap[c.parentCommentId]) {
                commentMap[c.parentCommentId].replies.push(commentMap[c.id]);
            } else {
                topLevel.push(commentMap[c.id]);
            }
        });

        return topLevel;
    }, [comments]);

    const renderCommentBranch = (commentList) => {
        return commentList.map(c => (
            <div key={c.id} className="ml-4 sm:ml-6 border-l-2 border-slate-200 dark:border-slate-800 pl-3 sm:pl-4">
                <Comment comment={c} postAuthorId={post?.authorId} postId={postId} />
                {c.replies?.length > 0 && renderCommentBranch(c.replies)}
            </div>
        ));
    };

    if (loading) {
        return (
            <div className="py-16 text-center space-y-3">
                <div className="w-8 h-8 mx-auto rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
                <p className="text-xs text-slate-400 dark:text-slate-500">Loading conversation...</p>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="text-center py-20 bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl p-8 space-y-4 shadow-xs">
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Post Expired or Not Found</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Posts on Batcheet vanish after 24 hours.</p>
                <button
                    onClick={() => navigate('home')}
                    className="px-5 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-2xl text-xs transition-all shadow-sm hover:shadow-brand-500/20"
                >
                    Return to Feed
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-5 animate-fade-in pb-16">
            {/* Top Back Navigation */}
            <button
                onClick={() => navigate('home')}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors p-1"
            >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to feed</span>
            </button>

            {/* The Post */}
            <PostCard post={post} navigate={navigate} />

            {/* Conversation Header & Reply Form */}
            <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-200/80 dark:border-slate-800 pb-3">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-slate-100">
                        <MessageSquare className="w-4 h-4 text-brand-500" />
                        <span>Discussion ({comments.length})</span>
                    </div>
                </div>

                {/* Main Top-Level Comment Input */}
                <CommentForm postId={postId} />

                {/* Nested Comment Threads */}
                <div className="space-y-3 pt-2">
                    {commentThreads.map(c => (
                        <div key={c.id}>
                            <Comment comment={c} postAuthorId={post.authorId} postId={postId} />
                            {c.replies?.length > 0 && renderCommentBranch(c.replies)}
                        </div>
                    ))}

                    {comments.length === 0 && (
                        <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-8">
                            No comments yet. Be the first to start the discussion!
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PostPage;